import { config } from '@/config'
import { pressureInHg } from './units'
import type {
  NwsPoint,
  NwsForecast,
  NwsObservation,
  NwsAlert,
  NwsGeometry,
  ResolvedLocation,
} from './types'

/** Raised when NWS has no coverage for a point (non-US / offshore → /points 404). */
export class OutOfCoverageError extends Error {
  constructor(public lat: number, public lon: number) {
    super('This location is outside NWS coverage (US only).')
    this.name = 'OutOfCoverageError'
  }
}

async function nwsFetch<T>(path: string): Promise<T> {
  // path is relative to the proxy base, e.g. "/points/40.01,-105.27"
  const url = path.startsWith('http') ? path : `${config.nws.baseUrl}${path}`
  const res = await fetch(url, { headers: { Accept: 'application/geo+json' } })

  if (res.status === 404) {
    throw new Error(`NWS 404: ${path}`)
  }
  if (!res.ok) {
    throw new Error(`NWS ${res.status}: ${path}`)
  }
  return (await res.json()) as T
}

/** Round to 4 decimals — >4 triggers a 301 redirect to the rounded URL. */
function coord(lat: number, lon: number): string {
  return `${lat.toFixed(4)},${lon.toFixed(4)}`
}

export async function getPoint(lat: number, lon: number): Promise<NwsPoint> {
  try {
    const data = await nwsFetch<{ properties: NwsPoint }>(`/points/${coord(lat, lon)}`)
    return data.properties
  } catch (err) {
    if (err instanceof Error && err.message.includes('404')) {
      throw new OutOfCoverageError(lat, lon)
    }
    throw err
  }
}

export async function resolveLocation(lat: number, lon: number): Promise<ResolvedLocation> {
  const point = await getPoint(lat, lon)
  const { city, state } = point.relativeLocation.properties
  return { lat, lon, label: `${city}, ${state}`, point }
}

/** Last path segment of an NWS zone URL → UGC code (e.g. ".../county/COC013" → "COC013"). */
export function ugcFromUrl(url: string | undefined): string | null {
  if (!url) return null
  const seg = url.split('/').pop()
  return seg && /^[A-Z]{2}[CZ]\d{3}$/.test(seg) ? seg : (seg ?? null)
}

/** Append ?units=us so forecast temps come back in °F. */
function withUsUnits(url: string): string {
  return url.includes('?') ? `${url}&units=us` : `${url}?units=us`
}

export async function getForecast(point: NwsPoint): Promise<NwsForecast> {
  const data = await nwsFetch<{ properties: NwsForecast }>(withUsUnits(point.forecast))
  return data.properties
}

export async function getHourlyForecast(point: NwsPoint): Promise<NwsForecast> {
  const data = await nwsFetch<{ properties: NwsForecast }>(withUsUnits(point.forecastHourly))
  return data.properties
}

/** Latest observation from the nearest reporting station (values can be null). */
export async function getLatestObservation(point: NwsPoint): Promise<NwsObservation | null> {
  const stations = await nwsFetch<{ features: { id: string; properties: { stationIdentifier: string } }[] }>(
    point.observationStations,
  )
  const first = stations.features[0]
  if (!first) return null
  const stationId = first.properties.stationIdentifier
  try {
    const obs = await nwsFetch<{ properties: NwsObservation }>(
      `/stations/${stationId}/observations/latest`,
    )
    return obs.properties
  } catch {
    return null // a single dead station shouldn't break the home screen
  }
}

export interface PressureSample {
  time: number
  inHg: number
}
/** Recent barometric pressure (inHg) from the nearest station, oldest→newest (~last 4h). */
export async function getPressureSeries(point: NwsPoint): Promise<PressureSample[]> {
  const stations = await nwsFetch<{ features: { properties: { stationIdentifier: string } }[] }>(
    point.observationStations,
  )
  const stationId = stations.features[0]?.properties.stationIdentifier
  if (!stationId) return []
  const start = new Date(Date.now() - 4 * 3600_000).toISOString()
  try {
    const data = await nwsFetch<{ features: { properties: NwsObservation }[] }>(
      `/stations/${stationId}/observations?start=${encodeURIComponent(start)}&limit=24`,
    )
    const out: PressureSample[] = []
    for (const f of data.features) {
      const inHg = pressureInHg(f.properties.barometricPressure)
      const t = Date.parse(f.properties.timestamp)
      if (inHg != null && Number.isFinite(t)) out.push({ time: t, inHg })
    }
    return out.sort((a, b) => a.time - b.time)
  } catch {
    return []
  }
}

export type PressureTrend = 'rising' | 'falling' | 'steady'
/** Trend from the last ~3h: rising/falling if |Δ| ≥ 0.02 inHg, else steady. */
export function pressureTrend(series: PressureSample[]): PressureTrend {
  if (series.length < 2) return 'steady'
  const newest = series[series.length - 1]
  const target = newest.time - 3 * 3600_000
  let ref = series[0]
  for (const s of series) if (Math.abs(s.time - target) < Math.abs(ref.time - target)) ref = s
  const d = newest.inHg - ref.inHg
  if (d >= 0.02) return 'rising'
  if (d <= -0.02) return 'falling'
  return 'steady'
}

/** Active alerts for a point. Cancel/expired entries are filtered out. */
export async function getActiveAlerts(lat: number, lon: number): Promise<NwsAlert[]> {
  const data = await nwsFetch<{
    features: { geometry: NwsGeometry | null; properties: Omit<NwsAlert, 'geometry'> }[]
  }>(`/alerts/active?point=${coord(lat, lon)}`)
  const now = Date.now()
  return data.features
    // Fold the feature-level geometry into the alert; default parameters to {} so
    // Storm Mode's tag readers never hit undefined on older/zone-only alerts.
    .map((f) => ({ ...f.properties, geometry: f.geometry, parameters: f.properties.parameters ?? {} }))
    .filter((a) => a.messageType !== 'Cancel')
    // Fail OPEN on a missing/unparseable expiry — never silently drop a warning that
    // might still be active (a NaN comparison would otherwise read as expired).
    .filter((a) => {
      const t = a.expires ? Date.parse(a.expires) : NaN
      return !a.expires || !Number.isFinite(t) || t > now
    })
    .sort((a, b) => severityRank(b.severity) - severityRank(a.severity))
}

function severityRank(s: NwsAlert['severity']): number {
  return { Extreme: 5, Severe: 4, Moderate: 3, Minor: 2, Unknown: 1 }[s] ?? 0
}

export interface ActiveAlertCount {
  total: number
  land: number
  marine: number
  /** Active-alert tally per NWS area code (US state / territory / marine zone). */
  areas: Record<string, number>
}

/** Nationwide active-alert counts — a live "national pulse" signal with no R2 needed. */
export async function getActiveAlertCount(): Promise<ActiveAlertCount> {
  const data = await nwsFetch<{
    total: number
    land: number
    marine: number
    areas?: Record<string, number>
  }>('/alerts/active/count')
  return {
    total: data.total ?? 0,
    land: data.land ?? 0,
    marine: data.marine ?? 0,
    areas: data.areas ?? {},
  }
}
